'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Select } from '@/components/ui/Select';
import { videoReplicationsApi, type GenjutsuMode, type GenjutsuPreset, type GenjutsuQuote } from '@/lib/api/video-replications';

const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2';

/** Every asynchronous snapshot belongs to its exact model/settings and signed-in owner. */
export function KagemaneGenjutsuFields({ mode, onModeChange, presetId, onPresetChange, resolution, duration,
  ownerId, disabled, onQuoteChange, refreshKey = 0, showQuote = true, onCostChange,
}: {
  mode: GenjutsuMode; onModeChange: (mode: GenjutsuMode) => void;
  presetId: string; onPresetChange: (id: string) => void;
  resolution: string; duration?: number; ownerId?: string; disabled: boolean;
  onQuoteChange: (quote: GenjutsuQuote | null) => void;
  refreshKey?: number;
  showQuote?: boolean;
  onCostChange?: (snapshot: { quote: GenjutsuQuote | null; error: boolean }) => void;
}) {
  const t = useTranslations('kagemane');
  const [retry, setRetry] = useState(0);
  const [presets, setPresets] = useState<{ key: string; items: GenjutsuPreset[] } | null>(null);
  const [presetError, setPresetError] = useState(false);
  const [quote, setQuote] = useState<{ key: string; data: GenjutsuQuote } | null>(null);
  const [quoteError, setQuoteError] = useState(false);
  const videoModel = `higgsfield/genjutsu/${mode}/v1.0` as const;
  const validDuration = typeof duration === 'number' && Number.isFinite(duration) && duration >= 4 && duration <= 30;
  const quoteKey = JSON.stringify([ownerId, videoModel, resolution, duration, retry, refreshKey]);
  const presetKey = JSON.stringify([ownerId, retry]);
  const currentQuote = quote?.key === quoteKey ? quote.data : null;
  const items = presets && presets.key === presetKey ? presets.items : null;
  const presetValid = mode !== 'restyle' || Boolean(items?.some(item => item.id === presetId));
  const ready = Boolean(ownerId && validDuration && currentQuote && presetValid);

  useEffect(() => { onQuoteChange(ready ? currentQuote : null); }, [ready, currentQuote, quoteKey, presetId, onQuoteChange]);
  useEffect(() => { onCostChange?.({ quote: currentQuote, error: quoteError }); }, [currentQuote, quoteError, quoteKey, onCostChange]);
  useEffect(() => {
    let active = true;
    setPresets(null); setPresetError(false);
    if (mode !== 'restyle' || !ownerId) return;
    async function load() {
      try {
        const result = await videoReplicationsApi.genjutsuPresets();
        if (!result.success || !result.data || result.data.model !== 'higgsfield/genjutsu/restyle/v1.0'
          || !Array.isArray(result.data.items)) throw new Error('preset_snapshot_unavailable');
        if (active) setPresets({ key: presetKey, items: result.data.items });
      } catch { if (active) setPresetError(true); }
    }
    void load();
    return () => { active = false; };
  }, [mode, ownerId, retry, presetKey]);
  useEffect(() => {
    let active = true;
    setQuote(null); setQuoteError(false);
    if (!ownerId || !validDuration) return;
    async function load() {
      try {
        const result = await videoReplicationsApi.genjutsuQuote(videoModel, resolution, duration!);
        if (!result.success || !result.data || result.data.video_model !== videoModel
          || result.data.resolution !== resolution || !Number.isSafeInteger(result.data.credits) || result.data.credits <= 0
          || result.data.billable_seconds !== Math.ceil(duration!)) throw new Error('quote_snapshot_unavailable');
        if (active) setQuote({ key: quoteKey, data: result.data });
      } catch { if (active) setQuoteError(true); }
    }
    void load();
    return () => { active = false; };
  }, [quoteKey, ownerId, videoModel, resolution, duration, validDuration, retry]);

  return <div className="space-y-3 border-t border-[#DCE5EB] pb-1 pt-3">
    <div className="flex min-h-11 items-center justify-between gap-3">
      <label htmlFor="kagemane-genjutsu-mode" className="text-sm font-medium">{t('genjutsuMode')}</label>
      <Select id="kagemane-genjutsu-mode" ariaLabel={t('genjutsuMode')} value={mode} disabled={disabled} variant="brand"
        className="w-44 max-w-[65%] shrink-0" onChange={value => onModeChange(value as GenjutsuMode)}
        options={(['motion-transfer', 'object-swap', 'restyle'] as const).map(value => ({ value, label: t(`genjutsuModes.${value}`) }))} />
    </div>
    <p className="text-xs leading-5 text-[#53636D]">{t(`genjutsuHelp.${mode}`)}</p>
    {!ownerId ? <p role="status" className="text-xs leading-5 text-[#53636D]">{t('genjutsuSignIn')}</p> : <>
      {mode === 'restyle' && <fieldset disabled={disabled} className="min-w-0">
        <legend className="mb-2 text-sm font-medium">{t('genjutsuStyle')}</legend>
        {presetError ? <p role="alert" className="text-xs leading-5 text-red-600">{t('genjutsuPresetsError')}</p>
          : !items ? <p role="status" className="text-xs text-[#53636D]">{t('genjutsuPresetsLoading')}</p>
            : !items.length ? <p role="status" className="text-xs leading-5 text-[#53636D]">{t('genjutsuPresetsEmpty')}</p>
              : <div className="grid max-h-64 grid-cols-2 gap-2 overflow-y-auto p-1" role="group" aria-label={t('genjutsuStyle')}>
                {items.map(item => <button key={item.id} type="button" aria-pressed={presetId === item.id}
                  onClick={() => onPresetChange(item.id)} className={`overflow-hidden rounded-xl border bg-white text-left transition-colors disabled:opacity-50 ${presetId === item.id ? 'border-[#4ABDE9] ring-1 ring-[#4ABDE9]' : 'border-[#DCE5EB] hover:border-[#4ABDE9]'} ${focusClass}`}>
                  <img src={item.preview_url} alt="" loading="lazy" decoding="async" className="aspect-[4/3] w-full bg-[#E9EEF1] object-cover" />
                  <span className="block px-2 py-1.5 text-xs leading-5">{item.name}</span>
                </button>)}
              </div>}
        {items?.length && !presetValid ? <p role={presetId ? 'alert' : 'status'} className="mt-2 text-xs leading-5 text-[#8A421B]">{t(presetId ? 'genjutsuPresetUnavailable' : 'genjutsuSelectStyle')}</p> : null}
      </fieldset>}
      {showQuote && validDuration && <div aria-live="polite" aria-atomic="true" className="text-xs leading-5 text-[#53636D]">
        {quoteError ? <p role="alert" className="text-red-600">{t('genjutsuQuoteError')}</p>
          : currentQuote ? <p>{t('genjutsuQuote', { credits: currentQuote.credits, seconds: currentQuote.billable_seconds })}</p>
            : <p role="status">{t('genjutsuQuoteLoading')}</p>}
      </div>}
      {(presetError || (showQuote && quoteError) || (mode === 'restyle' && items?.length === 0) || (presetId && !presetValid)) && <button type="button" disabled={disabled}
        onClick={() => setRetry(value => value + 1)} className={`min-h-9 text-xs underline disabled:opacity-50 ${focusClass}`}>{t('genjutsuRetry')}</button>}
    </>}
  </div>;
}
